import { Component, Directive, TemplateRef, inject, contentChildren, input } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { AbstractControl, ValidationErrors } from '@angular/forms';

import { ApiError } from '../../common/ApiError';
import { errorCodeText } from '../../common/error-messages';
import { SERVER_ERROR_KEY } from '../../common/server-field-errors';

/**
 * A field's own sentence for one validator key, in place of the generic one — for rules the
 * generic texts cannot word, like a pattern or a custom validator. Stays `i18n` in the template:
 *
 *   <ng-template appFieldError="pattern"><span i18n>Die Root-URL muss … sein</span></ng-template>
 */
@Directive({ selector: 'ng-template[appFieldError]' })
export class FieldErrorDirective {
  template = inject<TemplateRef<unknown>>(TemplateRef);

  readonly key = input<string>(undefined, { alias: "appFieldError" });
}

/**
 * The generic messages (#199 decision 1a), worded by the same dictionary as the server's coded
 * errors (`error-messages.ts`), so a field says „Höchstens 100 Zeichen." whether the browser or
 * the backend noticed. They sit directly under the field and so do not repeat its name — which
 * German grammar would not allow generically anyway („des Ortes", „der PLZ"). A key with neither
 * an entry here nor an `appFieldError` shows no message; the red border still marks the field.
 */
const AS_API_ERROR: Record<string, (error: any) => ApiError> = {
  required: () => ({ code: 'required' }),
  maxlength: (e) => ({ code: 'maximumLength', params: { maxLength: e.requiredLength } }),
  minlength: (e) => ({ code: 'minimumLength', params: { minLength: e.requiredLength } }),
  email: () => ({ code: 'email' }),
  min: (e) => ({ code: 'greaterThanOrEqual', params: { comparisonValue: e.min } }),
  max: (e) => ({ code: 'lessThanOrEqual', params: { comparisonValue: e.max } }),
  pattern: () => ({ code: 'regularExpression' }),
  // salutationCodeValidator (Salutation.ts) refuses what the backend refuses as salutationInvalid.
  salutationCode: () => ({ code: 'salutationInvalid' }),
};

export interface FieldMessage {
  key: string;
  text?: string;
  template?: TemplateRef<unknown>;
}

/** Whether a control's client-side errors are due: after the user touched or typed (#199 2a). */
export function errorsDue(control: AbstractControl | null | undefined): boolean {
  return !!control && control.invalid && (control.dirty || control.touched);
}

/**
 * The messages for one control: its validators' once the user touched or typed, and a rejected
 * save's (`applyServerFieldErrors`, #178) whenever there is one. Used by `<app-form-row>`, and on
 * its own where a row holds more than one field.
 */
@Component({
  selector: 'app-field-errors',
  imports: [NgTemplateOutlet],
  template: `
    @for (message of messages; track message.key) {
      @if (message.key === serverKey) {
        <div class="invalid-feedback d-block" data-testid="server-error">{{ message.text }}</div>
      } @else {
        <div class="invalid-feedback d-block">
          @if (message.template) {
            <ng-container [ngTemplateOutlet]="message.template"></ng-container>
          } @else {
            {{ message.text }}
          }
        </div>
      }
    }
  `,
})
export class FieldErrorsComponent {
  readonly control = input<AbstractControl | null>();
  /** Field-specific sentences, by validator key; `<app-form-row>` passes its projected ones. */
  readonly custom = input<readonly FieldErrorDirective[]>([]);
  private readonly projected = contentChildren(FieldErrorDirective);

  protected readonly serverKey = SERVER_ERROR_KEY;

  get messages(): FieldMessage[] {
    const errors: ValidationErrors = this.control()?.errors ?? {};
    const due = errorsDue(this.control());
    const custom = [...this.custom(), ...this.projected()];
    const messages: FieldMessage[] = [];
    for (const key of Object.keys(errors)) {
      if (key === SERVER_ERROR_KEY) {
        if (typeof errors[key] === 'string') messages.push({ key, text: errors[key] });
        continue;
      }
      if (!due) continue;
      const own = custom.find((c) => c.key() === key);
      if (own) {
        messages.push({ key, template: own.template });
      } else if (AS_API_ERROR[key]) {
        const text = errorCodeText(AS_API_ERROR[key](errors[key]));
        if (text) messages.push({ key, text });
      }
    }
    return messages;
  }
}
